import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Autocomplete,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import api from "../services/api";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import KioskHeader from "../components/KioskHeader";
import CameraCapture from "../components/CameraCapture";
import { BRAND, cardSx, primaryButtonSx } from "../styles/authTheme";
import { isOnline } from "../services/syncQueue";

const FaceRegistrationPage = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [successState, setSuccessState] = useState(false);
  const [online, setOnline] = useState(isOnline());
  
  // Liveness States
  const [livenessState, setLivenessState] = useState("IDLE"); // IDLE, FRONT, BLINK, LEFT, RIGHT
  const [captureLoopActive, setCaptureLoopActive] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const frontImageRef = useRef(null);
  const loopTimeoutRef = useRef(null);

  const cameraRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await api.get("employer-employee/");
        const data = res.data.results || res.data;
        const formatted = data.map(emp => ({
          id: emp.id,
          label: `${emp.first_name} ${emp.last_name} (${emp.employee_id || emp.email})`,
          has_face: !!emp.face_embedding
        }));
        setEmployees(formatted);
      } catch (err) {
        console.error("Failed to fetch employees", err);
        toast.error("Failed to load employee list");
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    const handleOnlineChange = () => setOnline(isOnline());
    window.addEventListener("online", handleOnlineChange);
    window.addEventListener("offline", handleOnlineChange);
    return () => {
      window.removeEventListener("online", handleOnlineChange);
      window.removeEventListener("offline", handleOnlineChange);
    };
  }, []);

  const handleRegisterClick = () => {
    if (!selectedEmployee) {
      toast.error("Please select an employee first.");
      return;
    }
    if (selectedEmployee.has_face) {
      setConfirmDialogOpen(true);
    } else {
      startLivenessFlow();
    }
  };

  const startLivenessFlow = () => {
    setConfirmDialogOpen(false);
    setLoadingStatus(true);
    setLivenessState("FRONT");
    setCaptureLoopActive(true);
  };

  const finalizeRegistration = async (base64Front) => {
    try {
      const response = await api.post("ai/register-face/", {
        employee_id: selectedEmployee.id,
        image: base64Front,
      });
      if (response.data.success) {
        setSuccessState(true);
        selectedEmployee.has_face = true;
      }
    } catch (error) {
      const errMsg = error.response?.data?.error || "Registration failed.";
      toast.error(errMsg);
    } finally {
      setLoadingStatus(false);
      setLivenessState("IDLE");
    }
  };

  // Continuous Capture Loop
  useEffect(() => {
    if (!captureLoopActive || livenessState === "IDLE") return;

    let isSubscribed = true;

    const captureAndVerify = async () => {
      try {
        const photoBlob = await cameraRef.current?.capturePhoto?.();
        if (!photoBlob) {
           if(isSubscribed) loopTimeoutRef.current = setTimeout(captureAndVerify, 250);
           return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(photoBlob);
        reader.onloadend = async () => {
          if (!isSubscribed) return;
          const base64data = reader.result;

          try {
            const res = await api.post("ai/verify-liveness-action/", {
              action: livenessState.toLowerCase(),
              image: base64data
            });

            if (res.data.success && isSubscribed) {
              if (livenessState === "FRONT") {
                frontImageRef.current = base64data;
                setLivenessState("BLINK");
              } else if (livenessState === "BLINK") {
                setLivenessState("LEFT");
              } else if (livenessState === "LEFT") {
                setLivenessState("RIGHT");
              } else if (livenessState === "RIGHT") {
                setCaptureLoopActive(false);
                finalizeRegistration(frontImageRef.current);
                return; // Stop loop
              }
            }
          } catch (err) {
            // Wait for next frame
          }

          if (isSubscribed && captureLoopActive) {
            loopTimeoutRef.current = setTimeout(captureAndVerify, 250);
          }
        };
      } catch (e) {
        if (isSubscribed) {
          loopTimeoutRef.current = setTimeout(captureAndVerify, 250);
        }
      }
    };

    loopTimeoutRef.current = setTimeout(captureAndVerify, 250);

    return () => {
      isSubscribed = false;
      if (loopTimeoutRef.current) clearTimeout(loopTimeoutRef.current);
    };
  }, [captureLoopActive, livenessState]);

  const resetForm = () => {
    setSuccessState(false);
    setSelectedEmployee(null);
    setLivenessState("IDLE");
    setCaptureLoopActive(false);
    frontImageRef.current = null;
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: BRAND.bg, display: "flex", flexDirection: "column" }}>
      <KioskHeader
        title="Face Registration"
        subtitle="Register employee for Face AI"
        showBack
        backTo="/settings"
        online={online}
      />

      <Box
        sx={{
          flex: 1,
          px: { xs: 2, sm: 4 },
          py: { xs: 2, sm: 4 },
          maxWidth: 1000,
          width: "100%",
          mx: "auto",
        }}
      >
        <Grid container spacing={3}>
          {/* Left Column: Selection & Action */}
          <Grid item xs={12} md={5}>
            <Box sx={{ ...cardSx, height: "100%", display: "flex", flexDirection: "column" }}>
              <Typography
                variant="h6"
                sx={{ color: BRAND.navy, fontWeight: 700, mb: 2 }}
              >
                1. Select Employee
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  border: `1px solid ${BRAND.border}`,
                  mb: 3,
                  flexGrow: 1
                }}
              >
                <Autocomplete
                  id="employee-searchable-select"
                  options={employees}
                  value={selectedEmployee}
                  onChange={(event, newValue) => setSelectedEmployee(newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  fullWidth
                  disablePortal
                  disabled={successState || captureLoopActive}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Search & Select Employee" 
                      variant="outlined" 
                    />
                  )}
                />

                <Typography
                  variant="caption"
                  display="block"
                  sx={{ mt: 3, color: BRAND.muted, lineHeight: 1.6 }}
                >
                  Ensure the employee is facing the camera in a well-lit area. Only one face should be visible.
                </Typography>
              </Paper>

              {!successState && (
                <Button
                  fullWidth
                  variant="contained"
                  disableElevation
                  onClick={handleRegisterClick}
                  disabled={!selectedEmployee || loadingStatus}
                  sx={{
                    ...primaryButtonSx,
                    bgcolor: BRAND.orange,
                    py: 1.5,
                    "&:hover": {
                      bgcolor: "#cc5200",
                      boxShadow: "0 4px 14px rgba(230,90,0,0.25)",
                    },
                  }}
                >
                  {loadingStatus ? <CircularProgress size={24} color="inherit" /> : "Capture & Register Face"}
                </Button>
              )}
            </Box>
          </Grid>

          {/* Right Column: Camera or Success */}
          <Grid item xs={12} md={7}>
            <Box sx={{ ...cardSx, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              {successState ? (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 80, color: BRAND.green, mb: 2 }} />
                  <Typography variant="h5" sx={{ color: BRAND.navy, fontWeight: 700, mb: 1 }}>
                    Registration Successful!
                  </Typography>
                  <Typography variant="body1" sx={{ color: BRAND.muted, mb: 4 }}>
                    {selectedEmployee?.label} has been registered and can now use face punch.
                  </Typography>
                  
                  <Button
                    variant="contained"
                    disableElevation
                    onClick={resetForm}
                    sx={{
                      ...primaryButtonSx,
                      bgcolor: BRAND.navy,
                      px: 4,
                      "&:hover": {
                        bgcolor: "#1a2538",
                      },
                    }}
                  >
                    Register Next Employee
                  </Button>
                </Box>
              ) : (
                <>
                  <Typography
                    variant="h6"
                    sx={{ color: BRAND.navy, fontWeight: 700, mb: 1 }}
                  >
                    2. Capture Face
                  </Typography>
                  
                  {livenessState !== "IDLE" && (
                     <Paper sx={{ p: 2, mb: 2, bgcolor: BRAND.orange, color: '#fff', textAlign: 'center', borderRadius: '8px' }}>
                        <Typography variant="h6">
                          {livenessState === "FRONT" && "Look straight at the camera..."}
                          {livenessState === "BLINK" && "Close your eyes and HOLD them closed..."}
                          {livenessState === "LEFT" && "Turn your face to the LEFT..."}
                          {livenessState === "RIGHT" && "Turn your face to the RIGHT..."}
                        </Typography>
                     </Paper>
                  )}

                  <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
                    <CameraCapture ref={cameraRef} />
                  </Box>
                  
                  <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                    <DialogTitle>Already Registered</DialogTitle>
                    <DialogContent>
                      <DialogContentText>
                        You have already done face registration. Do you want to change face image?
                      </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ p: 2, gap: 1 }}>
                      <Button 
                        onClick={() => setConfirmDialogOpen(false)}
                        variant="outlined"
                        sx={{ color: BRAND.navy, borderColor: BRAND.navy, borderRadius: '8px', px: 3 }}
                      >
                        No
                      </Button>
                      <Button 
                        onClick={startLivenessFlow} 
                        variant="contained"
                        disableElevation
                        sx={{ bgcolor: BRAND.orange, color: '#fff', borderRadius: '8px', px: 3, '&:hover': { bgcolor: '#cc5200' } }}
                      >
                        Yes
                      </Button>
                    </DialogActions>
                  </Dialog>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default FaceRegistrationPage;
